/* eslint-disable prefer-arrow/prefer-arrow-functions */
import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';
import range from 'lodash/range';
import * as React from 'react';
import AutoSizer from 'react-virtualized/dist/es/AutoSizer';
import {
  IAsymmetryResult,
  IAsymmetryResultData,
} from '../../../data/providers/reports/asymmetryReportProvider';
import {
  EntityType,
  ISelectableEntity,
  ISize,
  ITag,
  ITestType,
} from '../../../data/types';
import { roundValue } from '../../../utils/math';
import { testMetaString } from '../../../utils/testMetaString';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { Card } from '../../shared/card/Card';
import { GraphContainer } from '../../shared/graphs/GraphContainer';
import { EPdfMode, shouldInvert } from '../../shared/pdf/Pdf';
import { SVGCenterText } from '../utils/SVGCenterText';
import { ASYMMETRY_COLORS } from './utils';

interface IProps {
  data: IAsymmetryResult[];
  entityType: EntityType;
  pdfMode: EPdfMode;
}

const XAXIS_PADDING = 20;
const BAR_HEIGHT = 70;
const BAR_SPACING = BAR_HEIGHT / 4;
const XAXIS_HEIGHT = 20;
const XTICK_COUNT = 10;
const GRAPH_TOP_PADDING = 20;
const GRAPH_BOTTOM_PADDING = 10;
const ZERO_LINE_WIDTH = 2;
const SECTION_GAP = 20;

function calculateResultHeight(result: IAsymmetryResult): number {
  return (
    result.data.length * (BAR_HEIGHT + BAR_SPACING) + BAR_SPACING + SECTION_GAP
  );
}

function calculateTotalHeight(results: IAsymmetryResult[]): number {
  return results.reduce((acc: number, result: IAsymmetryResult): number => {
    return acc + calculateResultHeight(result);
  }, XAXIS_HEIGHT + GRAPH_BOTTOM_PADDING + GRAPH_TOP_PADDING);
}

export const Graph = (props: IProps): React.ReactElement => {
  return (
    <Row>
      <Card
        noTopRadius
        inverse={shouldInvert(props.pdfMode)}
        style={{
          padding: '0px',
        }}
      >
        <Column>
          <GraphContainer
            inverse={shouldInvert(props.pdfMode)}
            style={{
              height: calculateTotalHeight(props.data),
              minHeight: 100,
            }}
          >
            <AutoSizer>
              {(size: ISize): React.ReactElement => {
                return (
                  <BarChart
                    data={props.data}
                    width={size.width}
                    height={size.height}
                    pdfMode={props.pdfMode}
                  />
                );
              }}
            </AutoSizer>
          </GraphContainer>
        </Column>
      </Card>
    </Row>
  );
};

interface IBarChartProps {
  data: IAsymmetryResult[];
  width: number;
  height: number;
  pdfMode: EPdfMode;
}
/**
 * @description  Bar Chart component
 * @param props
 * @returns
 */
function BarChart(props: IBarChartProps): React.ReactElement {
  const xAxisDomain = [20, -20];
  const xAxisDomainLength = Math.abs(xAxisDomain[1] - xAxisDomain[0]);
  const xAxisWidth = props.width - XAXIS_PADDING * 2;

  const valueScaler = React.useMemo((): number => {
    return xAxisDomain[1] - xAxisDomain[0] >= 0 ? 1 : -1;
  }, []);

  // Helper function to scale X-axis values
  const scaleX = (value: number): number => {
    const anchor = valueScaler === 1 ? xAxisDomain[0] : xAxisDomain[1];
    const normalizedValue = (value * valueScaler - anchor) / xAxisDomainLength;

    let pixel = normalizedValue * xAxisWidth;

    pixel = Math.max(0, Math.min(pixel, xAxisWidth));

    return pixel + XAXIS_PADDING;
  };

  return (
    <svg width={props.width} height={props.height}>
      <DominanceLabels
        width={props.width}
        scaleX={scaleX}
        valueScaler={valueScaler}
      />

      <Grid
        scaleX={scaleX}
        valueScaler={valueScaler}
        height={props.height}
        width={props.width}
        xMin={xAxisDomain[0]}
        xMax={xAxisDomain[1]}
        steps={XTICK_COUNT}
      />

      {flatMap(
        props.data,
        (
          section: IAsymmetryResult,
          sectionIndex: number
        ): React.ReactElement[] => {
          const resultHeight = calculateResultHeight(section);
          const yStart = sectionIndex * resultHeight + GRAPH_TOP_PADDING;
          const barsHeight = section.data.length * (BAR_HEIGHT + BAR_SPACING);
          const barsOffset = (resultHeight - barsHeight) / 2 + yStart;

          return section.data.map(
            (
              resultData: IAsymmetryResultData,
              metricIndex: number
            ): React.ReactElement => {
              const scoreLtr = resultData.score * valueScaler > 0;
              const baselineLtr = resultData.baseline.mean * valueScaler > 0;

              return (
                <React.Fragment key={`${sectionIndex}-${metricIndex}`}>
                  {resultData.baseline.mean !== undefined &&
                    resultData.baseline.mean !== null && (
                      <Bar
                        keyPrefix={`${sectionIndex}-${metricIndex}-baseline`}
                        fill={ASYMMETRY_COLORS.baseline}
                        x={
                          baselineLtr
                            ? scaleX(resultData.baseline.mean) - 1
                            : scaleX(0) + 1
                        }
                        y={
                          barsOffset +
                          metricIndex * (BAR_HEIGHT + BAR_SPACING) -
                          BAR_HEIGHT * 0.1
                        }
                        height={BAR_HEIGHT + BAR_HEIGHT * 0.2}
                        width={Math.abs(
                          baselineLtr
                            ? scaleX(resultData.baseline.mean) - scaleX(0)
                            : scaleX(0) - (scaleX(resultData.baseline.mean) - 1)
                        )}
                        scaleX={scaleX}
                        data={resultData}
                        ltr={baselineLtr}
                        border={props.pdfMode === EPdfMode.PRINT}
                        borderColor={
                          props.pdfMode === EPdfMode.PRINT
                            ? 'black'
                            : 'transparent'
                        }
                      />
                    )}
                  <ScoreBar
                    keyPrefix={`${sectionIndex}-${metricIndex}`}
                    x={scoreLtr ? scaleX(resultData.score) - 1 : scaleX(0) + 1}
                    y={barsOffset + metricIndex * (BAR_HEIGHT + BAR_SPACING)}
                    height={BAR_HEIGHT}
                    width={Math.abs(
                      scoreLtr
                        ? scaleX(resultData.score) - scaleX(0)
                        : scaleX(0) - (scaleX(resultData.score) - 1)
                    )}
                    scaleX={scaleX}
                    data={resultData}
                    ltr={scoreLtr}
                    fill={ASYMMETRY_COLORS[resultData.flag]}
                  />
                </React.Fragment>
              );
            }
          );
        }
      )}
    </svg>
  );
}

interface IBarProps {
  keyPrefix: string;
  x: number;
  y: number;
  width: number;
  height: number;
  scaleX: (value: number) => number;
  data: IAsymmetryResultData;
  ltr: boolean;
  fill: string;
  border?: boolean;
  borderColor?: string;
}

/**
 * @description Bar and Labels
 * @param props
 * @returns
 */
function Bar(props: IBarProps): React.ReactElement {
  const path = props.ltr
    ? `M ${props.x - props.width + ZERO_LINE_WIDTH},${props.y} L ${
        props.x - 5
      },${props.y} a5,5 0 0 1 5,5  V ${
        props.y + props.height - 5
      } a-5,5 0 0 1 -5,5 L ${props.x - props.width + ZERO_LINE_WIDTH},${
        props.y + props.height
      } Z`
    : `M ${props.x - props.width},${props.y + 5} a5,5 0 0 1 5,-5  L ${
        props.x - ZERO_LINE_WIDTH
      },${props.y}  L ${props.x - ZERO_LINE_WIDTH},${
        props.y + props.height
      } L ${props.x - props.width + 5},${
        props.y + props.height
      } a5,5 0 0 1 -5,-5 Z`;

  return (
    <path
      d={path}
      fill={props.fill}
      stroke={props.borderColor ?? 'transparent'}
      strokeDasharray={props.border ? '3 1' : 'none'}
      strokeWidth={1.5}
    />
  );
}

function ScoreBar(props: IBarProps): React.ReactElement {
  const textX = props.ltr ? 0 : props.scaleX(0) + 10;
  return (
    <g>
      <Bar
        keyPrefix={props.keyPrefix}
        x={props.x}
        y={props.y}
        height={BAR_HEIGHT}
        width={props.width}
        scaleX={props.scaleX}
        data={props.data}
        ltr={props.ltr}
        fill={props.fill}
      />
      <BarLabel
        x={textX}
        y={props.y}
        height={props.height}
        data={props.data}
        scaleX={props.scaleX}
        ltr={props.ltr}
      />
    </g>
  );
}

interface IBarLabelProps {
  x: number;
  y: number;
  data: IAsymmetryResultData;
  scaleX: (value: number) => number;
  ltr: boolean;
  height: number;
}

interface IBarLabelPositions {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  dotX: number;
  dotY: number;
  dotRadius: number;
}

/**
 * @description Bar Label component
 * @param props
 * @returns
 */
function BarLabel(props: IBarLabelProps): React.ReactElement {
  const textRef = React.useRef(null);
  const [positions, setPositions] = React.useState<IBarLabelPositions>({
    x1: 0,
    x2: 0,
    y1: 0,
    y2: 0,
    dotX: 0,
    dotY: 0,
    dotRadius: 0,
  });

  const [opacity, setOpacity] = React.useState(0);

  React.useEffect((): void => {
    if (textRef.current) {
      const { height, width } = textRef.current.getBBox();
      const dotRadius = (props.height * 0.6) / 2;
      const x1 = props.ltr
        ? props.scaleX(0) - dotRadius
        : props.scaleX(0) + dotRadius;
      const x2 = x1 + width;
      const y1 = props.y + props.height / 2 - height / 2;
      const y2 = y1 + height;

      const dotX = props.scaleX(0);

      const dotY = props.y + props.height / 2 - (dotRadius * 2) / 2;

      setPositions({
        x1,
        x2,
        y1,
        y2,
        dotX,
        dotY,
        dotRadius,
      });

      setTimeout((): void => {
        setOpacity(1);
      }, 100);
    }
  }, [props.ltr, props.scaleX, props.y, props.height]);

  const score = React.useMemo((): string => {
    const { units, roundingPrecision } = props.data;
    if (props.data.left !== null && props.data.right !== null) {
      return `L: ${roundValue(
        props.data.left,
        roundingPrecision
      )}${units} R: ${roundValue(props.data.right, roundingPrecision)}${units}`;
    } else {
      return '';
    }
  }, [JSON.stringify(props.data)]);

  const baseline = React.useMemo((): string => {
    if (
      props.data.baseline.mean !== undefined &&
      props.data.baseline.mean !== null
    ) {
      return `Baseline: ${roundValue(props.data.baseline.mean)}%`;
    }
    return '';
  }, [JSON.stringify(props.data)]);

  return (
    <React.Fragment>
      <path
        d={`
           M ${positions.dotX},${positions.dotY}
           A ${positions.dotRadius},${positions.dotRadius} 0 1,1 ${
          positions.dotX - 0.01
        },${positions.dotY} Z
         `}
        fill='var(--color-surface-6)'
        stroke='var(--color-graph-dot-stroke)'
        strokeWidth='2'
        opacity={opacity}
      />
      {/* DOT TEXT */}
      <SVGCenterText
        xDomain={[
          positions.dotX - positions.dotRadius,
          positions.dotX + positions.dotRadius,
        ]}
        yDomain={[positions.dotY, positions.dotY + positions.dotRadius * 2]}
        text={`${Math.round(props.data.score)}%`}
        textAttrs={{
          fill: 'var(--color-graph-dot-text)',
          fontSize: positions.dotRadius * 0.7,
        }}
      />

      <text
        ref={textRef}
        x={positions.x1}
        y={positions.y1}
        fontSize='12'
        fill='var(--color-text)'
        opacity={opacity}
        dominantBaseline='hanging'
      >
        <tspan
          x={positions.x1 + (props.ltr ? -3 : 3)}
          opacity={opacity}
          textAnchor={props.ltr ? 'end' : 'start'}
        >
          {testMetaString(
            (props.data.metric.testType.entity as ITestType).abbr,
            props.data.metric.tags.map(
              (tag: ISelectableEntity): ITag => tag.entity as ITag
            )
          )}
        </tspan>
        <tspan
          x={positions.x1 + (props.ltr ? -5 : 5)}
          dy='14'
          opacity={opacity}
          textAnchor={props.ltr ? 'end' : 'start'}
        >
          {props.data.metric.metric.label}
        </tspan>
        <tspan
          x={positions.x1 + (props.ltr ? -5 : 5)}
          dy='14'
          opacity={opacity}
          textAnchor={props.ltr ? 'end' : 'start'}
        >
          {score}
        </tspan>
        {!isEmpty(baseline) && (
          <tspan
            x={positions.x1 + (props.ltr ? -3 : 3)}
            dy='14'
            opacity={opacity}
            textAnchor={props.ltr ? 'end' : 'start'}
            fill={ASYMMETRY_COLORS[props.data.baselineFlag]}
          >
            {baseline}
          </tspan>
        )}
      </text>
    </React.Fragment>
  );
}

interface IDominantLabelsProps {
  width: number;
  scaleX: (value: number) => number;
  valueScaler: number;
}

function DominanceLabels(props: IDominantLabelsProps): React.ReactElement {
  const midpoint = (props.width - XAXIS_PADDING * 2) / 2;
  const leftDomain = [XAXIS_PADDING, midpoint + XAXIS_PADDING];
  const rightDomain = [midpoint + XAXIS_PADDING, props.width - XAXIS_PADDING];

  return (
    <g>
      <SVGCenterText
        xDomain={leftDomain}
        yDomain={[0, 20]}
        text={props.valueScaler < 0 ? 'Left Dominant' : 'Right Dominant'}
        textAttrs={{
          y: 10,
          fill: 'var(--color-text)',
          fontSize: 14,
        }}
      />
      <SVGCenterText
        xDomain={rightDomain}
        yDomain={[0, 20]}
        text={props.valueScaler > 0 ? 'Left Dominant' : 'Right Dominant'}
        textAttrs={{
          y: 10,
          fill: 'var(--color-text)',
          fontSize: 14,
        }}
      />
    </g>
  );
}

interface IGridProps {
  scaleX: (value: number) => number;
  valueScaler: number;
  height: number;
  width: number;
  xMin: number;
  xMax: number;
  steps: number;
}

/**
 * @description Grid component
 * @param props
 * @returns
 */
function Grid(props: IGridProps): React.ReactElement {
  const gridLineAttrs = {
    stroke: 'var(--color-graph-grid-lines)',
    strokeDasharray: '3 3',
  };

  const stepValue = React.useMemo((): number => {
    return ((props.xMax - props.xMin) / props.steps) * props.valueScaler;
  }, [props.xMax, props.xMin, props.steps]);

  const stepMapper = React.useMemo(
    (): ((value: number) => number) =>
      (stepIndex: number): number => {
        return stepIndex * (stepValue * props.valueScaler) + props.xMin;
      },
    [props.xMax, props.xMin, stepValue]
  );

  return (
    <React.Fragment>
      {/* Draw X Bottom Axis */}
      <line
        x1={XAXIS_PADDING}
        y1={props.height - XAXIS_HEIGHT}
        x2={props.width - XAXIS_PADDING}
        y2={props.height - XAXIS_HEIGHT}
        {...gridLineAttrs}
      />
      {/* Draw X Top Axis */}
      <line
        x1={XAXIS_PADDING}
        y1={GRAPH_TOP_PADDING}
        x2={props.width - XAXIS_PADDING}
        y2={GRAPH_TOP_PADDING}
        stroke='var(--color-graph-grid-lines)'
        {...gridLineAttrs}
      />

      {range(props.steps + 1)
        .map(stepMapper)
        .map((tick: number, index: number): React.ReactElement => {
          return (
            <React.Fragment key={`grid-${index}`}>
              <line
                key={`grid-xline-${index}`}
                x1={props.scaleX(tick)}
                y1={GRAPH_TOP_PADDING}
                x2={props.scaleX(tick)}
                y2={props.height - XAXIS_HEIGHT}
                {...gridLineAttrs}
                /// apply special styling for the 0 tick
                {...(tick === 0
                  ? {
                      stroke: 'var(--color-graph-grid-lines)',
                      strokeDasharray: '0',
                      strokeWidth: ZERO_LINE_WIDTH,
                    }
                  : {})}
              />
              <g
                key={`grid-tick-${index}`}
                transform={`translate(${props.scaleX(tick)}, ${
                  props.height - XAXIS_HEIGHT
                })`}
              >
                <text
                  y='20'
                  textAnchor='middle'
                  fontSize='12'
                  fill='var(--color-text)'
                >
                  {tick}
                </text>
              </g>
            </React.Fragment>
          );
        })}
    </React.Fragment>
  );
}
